import {
  cmToInches,
  DEFAULT_SCREEN_CALIBRATION,
  estimateDistanceCm,
  inchesToCm,
  sanitizeScreenCalibration,
  screenAspect,
  type ScreenCalibrationData,
} from '../perception/ScreenCalibration';
import { button, el } from './dom';

export interface ScreenCalibrationWizardCallbacks {
  /** The values to open with (stored calibration, or the defaults). */
  getInitial: () => ScreenCalibrationData;
  /** The current raw iris separation, or null with no face. */
  getSeparation: () => number | null;
  /** True while the tracker is running, so the camera estimate is possible. */
  isTracking: () => boolean;
  /** Completed: persist these values. */
  onSave: (data: ScreenCalibrationData) => void;
  /** Dismissed without calibrating. */
  onClose: () => void;
}

type Step = 'intro' | 'screen' | 'distance' | 'test';

/** Common 16:9 sizes by diagonal, as [widthCm, heightCm]. */
const SCREEN_PRESETS: Array<[string, number]> = [
  ['13"', 33.8],
  ['15.6"', 40.5],
  ['24"', 62.3],
  ['27"', 70.1],
];

/** Sample window for the camera distance estimate. */
const ESTIMATE_MS = 1500;
const ESTIMATE_INTERVAL = 50;

/**
 * The screen & distance calibration wizard.
 *
 * A small modal over the faded boot menu that fills in the two numbers the
 * off-axis window needs to be physically correct: how big the screen is and how
 * far the player sits from it. It follows the reference viewer's flow — intro,
 * screen size, distance, live test — and only writes the calibration on
 * Complete, so Skip leaves the shipped defaults untouched.
 */
export class ScreenCalibrationWizard {
  readonly root = el('div', 'cal-wizard hidden');

  private step: Step = 'intro';
  private widthCm = DEFAULT_SCREEN_CALIBRATION.screenWidthCm;
  private heightCm = DEFAULT_SCREEN_CALIBRATION.screenHeightCm;
  private distanceCm = DEFAULT_SCREEN_CALIBRATION.viewingDistanceCm;
  private useInches = false;
  private estimating = false;

  private readonly title = el('h2', 'cal-wizard-title');
  private readonly body = el('div', 'cal-wizard-body');
  private readonly actions = el('div', 'cal-wizard-actions');

  constructor(
    parent: HTMLElement,
    private readonly callbacks: ScreenCalibrationWizardCallbacks,
  ) {
    const panel = el('div', 'cal-wizard-panel');
    panel.append(this.title, this.body, this.actions);
    this.root.append(panel);
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.close();
    });
    parent.append(this.root);
  }

  get visible(): boolean {
    return !this.root.classList.contains('hidden');
  }

  /** Open on the first step with the stored values. */
  open(): void {
    const initial = sanitizeScreenCalibration(this.callbacks.getInitial());
    this.widthCm = initial.screenWidthCm;
    this.heightCm = initial.screenHeightCm;
    this.distanceCm = initial.viewingDistanceCm;
    this.useInches = false;
    this.step = 'intro';
    this.root.classList.remove('hidden');
    this.render();
  }

  close(): void {
    this.estimating = false;
    this.root.classList.add('hidden');
  }

  /** Close and tell the game the wizard was dismissed. */
  private skip(): void {
    this.close();
    this.callbacks.onClose();
  }

  private complete(): void {
    const data = sanitizeScreenCalibration({
      screenWidthCm: this.widthCm,
      screenHeightCm: this.heightCm,
      viewingDistanceCm: this.distanceCm,
      calibrated: true,
    });
    this.close();
    this.callbacks.onSave(data);
  }

  private go(step: Step): void {
    this.step = step;
    this.render();
  }

  // ------------------------------------------------------------------ render

  private render(): void {
    this.body.replaceChildren();
    this.actions.replaceChildren();
    switch (this.step) {
      case 'intro':
        this.renderIntro();
        break;
      case 'screen':
        this.renderScreen();
        break;
      case 'distance':
        this.renderDistance();
        break;
      case 'test':
        this.renderTest();
        break;
    }
  }

  private renderIntro(): void {
    this.title.textContent = 'CALIBRATE SCREEN';
    this.body.append(
      el(
        'p',
        'cal-wizard-text',
        'The window effect is only convincing when the game knows your screen and how far you sit from it. Two measurements and you are done.',
      ),
      el(
        'p',
        'cal-wizard-hint',
        'You will need a ruler or tape measure. Measure the visible picture, not the bezel.',
      ),
    );
    this.actions.append(
      button('Skip', () => this.skip(), 'cal-wizard-button'),
      button('Start', () => this.go('screen'), 'cal-wizard-button cal-wizard-primary'),
    );
  }

  private renderScreen(): void {
    this.title.textContent = 'SCREEN SIZE';
    this.body.append(el('p', 'cal-wizard-text', 'Measure the visible screen area.'));

    const units = el('div', 'cal-wizard-units');
    units.append(this.unitButton('cm', false), this.unitButton('in', true));
    this.body.append(units);

    this.body.append(
      this.numberField('Width', this.widthCm, (cm) => {
        this.widthCm = cm;
        this.renderAspect();
      }),
      this.numberField('Height', this.heightCm, (cm) => {
        this.heightCm = cm;
        this.renderAspect();
      }),
    );

    const presets = el('div', 'cal-wizard-presets');
    for (const [label, diagonalInches] of SCREEN_PRESETS) {
      presets.append(button(label, () => this.applyPreset(diagonalInches), 'cal-wizard-chip'));
    }
    this.body.append(el('p', 'cal-wizard-hint', 'Common 16:9 sizes:'), presets);
    this.aspectLine = el('p', 'cal-wizard-aspect');
    this.body.append(this.aspectLine);
    this.renderAspect();

    this.actions.append(
      button('Back', () => this.go('intro'), 'cal-wizard-button'),
      button('Next', () => this.go('distance'), 'cal-wizard-button cal-wizard-primary'),
    );
  }

  private aspectLine: HTMLElement | null = null;

  private renderAspect(): void {
    if (!this.aspectLine) return;
    const data = {
      ...DEFAULT_SCREEN_CALIBRATION,
      screenWidthCm: this.widthCm,
      screenHeightCm: this.heightCm,
    };
    this.aspectLine.textContent = `Aspect ratio ${screenAspect(data).toFixed(2)}:1`;
  }

  private renderDistance(): void {
    this.title.textContent = 'VIEWING DISTANCE';
    this.body.append(
      el('p', 'cal-wizard-text', 'How far are your eyes from the screen, normally?'),
    );
    this.body.append(
      this.numberField('Distance', this.distanceCm, (cm) => {
        this.distanceCm = cm;
      }),
    );
    this.body.append(
      el('p', 'cal-wizard-hint', 'Typical: 50–60 cm at a laptop, 60–80 cm at a desk.'),
    );

    const estimate = button(
      'Estimate from camera',
      () => void this.estimateDistance(),
      'cal-wizard-button cal-wizard-estimate',
    );
    estimate.disabled = !this.callbacks.isTracking();
    this.estimateButton = estimate;
    this.body.append(estimate, (this.estimateStatus = el('p', 'cal-wizard-hint')));

    this.actions.append(
      button('Back', () => this.go('screen'), 'cal-wizard-button'),
      button('Next', () => this.go('test'), 'cal-wizard-button cal-wizard-primary'),
    );
  }

  private estimateButton: HTMLButtonElement | null = null;
  private estimateStatus: HTMLElement | null = null;

  private async estimateDistance(): Promise<void> {
    if (this.estimating || !this.callbacks.isTracking()) return;
    this.estimating = true;
    if (this.estimateButton) this.estimateButton.disabled = true;
    if (this.estimateStatus) this.estimateStatus.textContent = 'Hold still…';

    const samples: number[] = [];
    const started = Date.now();
    while (Date.now() - started < ESTIMATE_MS) {
      const separation = this.callbacks.getSeparation();
      if (separation !== null) samples.push(separation);
      await new Promise((resolve) => setTimeout(resolve, ESTIMATE_INTERVAL));
    }
    this.estimating = false;
    if (this.estimateButton) this.estimateButton.disabled = false;

    if (samples.length === 0) {
      if (this.estimateStatus) this.estimateStatus.textContent = 'No face seen — try again.';
      return;
    }
    const mean = samples.reduce((total, value) => total + value, 0) / samples.length;
    const estimate = estimateDistanceCm(mean);
    if (estimate === null) {
      if (this.estimateStatus) this.estimateStatus.textContent = 'Could not read your face.';
      return;
    }
    this.distanceCm = Math.round(estimate);
    // Refresh the field in place: a full render would discard the status line.
    const input = this.body.querySelector<HTMLInputElement>('.cal-wizard-input');
    if (input) {
      input.value = this.useInches
        ? cmToInches(this.distanceCm).toFixed(1)
        : String(this.distanceCm);
    }
    if (this.estimateStatus) {
      this.estimateStatus.textContent = `Estimated ${this.distanceCm} cm — adjust if you like.`;
    }
  }

  private renderTest(): void {
    this.title.textContent = 'TEST';
    this.body.append(
      el('p', 'cal-wizard-text', 'Move your head. The chamber should read as a window.'),
      el('p', 'cal-wizard-hint', '• Moving left reveals the right side'),
      el('p', 'cal-wizard-hint', '• Moving right reveals the left side'),
      el('p', 'cal-wizard-hint', '• Up and down tilt the perspective'),
      el(
        'p',
        'cal-wizard-text',
        `Screen ${this.widthCm.toFixed(1)} × ${this.heightCm.toFixed(1)} cm · distance ${this.distanceCm.toFixed(0)} cm`,
      ),
      el(
        'p',
        'cal-wizard-hint',
        this.callbacks.isTracking()
          ? 'The head map behind this panel shows where you are.'
          : 'Start the camera to see the head map and test the movement.',
      ),
    );
    this.actions.append(
      button('Adjust', () => this.go('distance'), 'cal-wizard-button'),
      button('Reset', () => this.reset(), 'cal-wizard-button'),
      button('Complete', () => this.complete(), 'cal-wizard-button cal-wizard-primary'),
    );
  }

  private reset(): void {
    this.widthCm = DEFAULT_SCREEN_CALIBRATION.screenWidthCm;
    this.heightCm = DEFAULT_SCREEN_CALIBRATION.screenHeightCm;
    this.distanceCm = DEFAULT_SCREEN_CALIBRATION.viewingDistanceCm;
    this.render();
  }

  private applyPreset(diagonalInches: number): void {
    // 16:9 from the diagonal: w = d·16/√337, h = d·9/√337.
    const diagonal = Math.hypot(16, 9);
    this.widthCm = (diagonalInches * 2.54 * 16) / diagonal;
    this.heightCm = (diagonalInches * 2.54 * 9) / diagonal;
    this.render();
  }

  private unitButton(label: string, inches: boolean): HTMLButtonElement {
    const node = button(
      label,
      () => {
        if (this.useInches === inches) return;
        this.useInches = inches;
        this.render();
      },
      'cal-wizard-chip',
    );
    node.classList.toggle('active', this.useInches === inches);
    return node;
  }

  private numberField(label: string, valueCm: number, onChange: (cm: number) => void): HTMLElement {
    const wrapper = el('label', 'cal-wizard-field');
    const shown = this.useInches ? cmToInches(valueCm) : valueCm;
    const input = el('input', 'cal-wizard-input') as unknown as HTMLInputElement;
    input.type = 'number';
    input.step = this.useInches ? '0.1' : '0.5';
    input.value = shown.toFixed(this.useInches ? 1 : 0);
    input.addEventListener('change', () => {
      const parsed = Number(input.value);
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      onChange(this.useInches ? inchesToCm(parsed) : parsed);
    });
    wrapper.append(
      el('span', 'cal-wizard-label', `${label} (${this.useInches ? 'in' : 'cm'})`),
      input,
    );
    return wrapper;
  }
}
